/**
 * Evidence engine (MERGE-PLAN §4). Pure and deterministic.
 *
 * Fuses the control results emitted by the ported audit modules with the unified
 * catalog (framework citations, objective, remediation) and the query log
 * (provenance) into a single exportable EvidencePack. Also computes the posture
 * score and per-framework coverage.
 */
import { getControl } from '../catalog';
import { computePostureScore, scoreFramework } from '../scoring';
import { CERTIFIABLE_FRAMEWORKS, type ControlId, type FrameworkId } from '../types';
import type {
  ControlResult,
  EvidenceContext,
  EvidencePack,
  EvidenceRecord,
} from './types';

const ALL_FRAMEWORKS: readonly FrameworkId[] = [...CERTIFIABLE_FRAMEWORKS, 'secScore'];

export function buildEvidencePack(
  results: readonly ControlResult[],
  context: EvidenceContext,
): EvidencePack {
  const collectedAt = new Date().toISOString();

  const records: EvidenceRecord[] = [];
  const testedIds: ControlId[] = [];
  const failedIds: ControlId[] = [];

  for (const r of results) {
    const control = getControl(r.controlId);
    if (!control) continue; // unknown key — ignore rather than fabricate a citation
    testedIds.push(control.id);
    if (r.status === 'fail') failedIds.push(control.id);

    records.push({
      controlId: control.id,
      title: control.title,
      objective: control.objective,
      module: control.module,
      severity: control.severity,
      status: r.status,
      affected: r.affected,
      provenance: { endpoints: r.endpoints, collectedAt },
      frameworks: control.frameworks,
      remediation: control.remediation,
    });
  }

  // Fail first, then partial, then pass; within a status, by severity.
  const statusRank = { fail: 0, partial: 1, pass: 2, not_tested: 3 } as const;
  const sevRank = { critical: 0, high: 1, medium: 2, low: 3 } as const;
  records.sort(
    (a, b) =>
      statusRank[a.status] - statusRank[b.status] ||
      sevRank[a.severity] - sevRank[b.severity] ||
      a.title.localeCompare(b.title),
  );

  const posture = computePostureScore(failedIds);
  const coverage = ALL_FRAMEWORKS.map((fw) => scoreFramework(fw, testedIds, failedIds));

  const controlsFailed = failedIds.length;
  const controlsTested = testedIds.length;

  return {
    meta: {
      ...context,
      collectedAt,
      controlsTested,
      controlsPassed: controlsTested - controlsFailed,
      controlsFailed,
    },
    records,
    posture,
    coverage,
  };
}
